import React, { useState } from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useDocumentStore } from '../stores/useDocumentStore';
import { useUIStore } from '../stores/useUIStore';
import { Search, Clock, CheckSquare, Compass, ChevronRight, X } from 'lucide-react';

export const Sidebar: React.FC = () => {
  const { currentPackage } = useWorkspaceStore();
  const { activeDocumentPath, setActiveDocument } = useDocumentStore();
  const { activeTab, setActiveTab, selectedRole, isSidebarOpen, setSidebarOpen } = useUIStore();
  const [filterText, setFilterText] = useState('');

  if (!currentPackage) {
    return (
      <aside className="w-64 glass-panel border-r border-white/10 p-4 font-mono text-xs text-slate-500">
        Loading package...
      </aside>
    );
  }

  const sortedDocs = [...currentPackage.documents].sort((a, b) => (a.numericOrder ?? 99) - (b.numericOrder ?? 99));

  const filteredDocs = sortedDocs.filter((doc) =>
    doc.name.toLowerCase().includes(filterText.toLowerCase()) ||
    (doc.meta.title && doc.meta.title.toLowerCase().includes(filterText.toLowerCase()))
  );

  const totalTasks = currentPackage.taskMatrix.length;
  const completedTasks = currentPackage.taskMatrix.filter((t) => t.status === 'completed').length;
  const percentComplete = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <aside
      className={`fixed md:relative inset-y-0 left-0 z-40 w-72 md:w-64 glass-panel border-r border-white/10 flex flex-col h-[calc(100vh-4rem)] shadow-2xl transition-transform duration-300 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}
    >
      {/* Sidebar Package Summary Card */}
      <div className="p-4 border-b border-white/10 bg-obsidian-900/60 space-y-3 relative">
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden absolute right-3 top-3 text-slate-400 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center justify-between pr-6 md:pr-0">
          <div className="truncate font-bold text-sm text-white tracking-tight">
            {currentPackage.name}
          </div>
          <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full font-mono uppercase font-semibold">
            {currentPackage.sourceType}
          </span>
        </div>

        {/* Progress Bar & Task Stats */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[11px] font-mono text-slate-400">
            <span className="flex items-center space-x-1">
              <CheckSquare className="w-3.5 h-3.5 text-emerald-400" />
              <span>Overall Progress</span>
            </span>
            <span className="text-emerald-400 font-bold">{percentComplete}%</span>
          </div>
          <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden border border-slate-800">
            <div
              style={{ width: `${percentComplete}%` }}
              className="bg-gradient-to-r from-blue-500 to-emerald-400 h-full rounded-full transition-all duration-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono pt-1">
          <span>{sortedDocs.length} Specification Docs</span>
          <span>{totalTasks} Tasks</span>
        </div>
      </div>

      {/* Role Explainer Trigger Button (Positioned DIRECTLY ABOVE Search Input) */}
      <div className="p-3 border-b border-white/10 bg-gradient-to-r from-blue-900/20 via-obsidian-900 to-indigo-900/20">
        <button
          onClick={() => {
            setActiveTab('explainer');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }}
          className={`w-full p-2.5 rounded-xl border transition-all text-xs flex items-center justify-between group font-mono ${
            activeTab === 'explainer'
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white border-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-obsidian-900/80 hover:bg-slate-800/80 text-slate-200 border-slate-700/80 hover:border-blue-500/40'
          }`}
          title="Open Role-Based Specification Explainer"
        >
          <div className="flex items-center space-x-2.5">
            <div className={`p-1.5 rounded-lg ${activeTab === 'explainer' ? 'bg-white/20 text-white' : 'bg-blue-500/10 text-blue-400'}`}>
              <Compass className="w-4 h-4 animate-pulse-slow" />
            </div>
            <div className="text-left min-w-0">
              <div className="font-bold text-xs leading-tight">Role Explainer</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider truncate font-semibold">
                {selectedRole} Mode
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
        </button>
      </div>

      {/* Filter Input */}
      <div className="px-3 py-2 border-b border-white/5 bg-obsidian-950/40">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Filter spec files..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full bg-obsidian-900/80 border border-slate-800 text-[11px] text-slate-200 rounded-lg pl-7 pr-2 py-1 focus:outline-none focus:border-blue-500 transition-colors font-mono placeholder:text-slate-600"
          />
        </div>
      </div>

      {/* Document Tree Navigation */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-mono flex items-center justify-between">
          <span>Specification Package</span>
          <span>{filteredDocs.length} Items</span>
        </div>

        {filteredDocs.map((doc) => {
          const isActive = activeTab === 'reader' && activeDocumentPath === doc.path;
          const docCompleted = doc.tasks.filter((t) => t.status === 'completed').length;
          const docTotal = doc.tasks.length;

          return (
            <button
              key={doc.path}
              onClick={() => {
                setActiveDocument(doc.path);
                setActiveTab('reader');
                if (window.innerWidth < 768) setSidebarOpen(false);
              }}
              className={`w-full text-left p-2.5 rounded-xl transition-all text-xs flex items-start space-x-2.5 group border ${
                isActive
                  ? 'bg-gradient-to-r from-blue-600/25 to-blue-500/10 border-blue-500/50 text-white shadow-lg shadow-blue-500/10'
                  : 'hover:bg-slate-800/40 text-slate-300 border-transparent'
              }`}
            >
              {/* Numeric Badge */}
              <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${
                isActive ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-slate-900 text-slate-500 border-slate-800 group-hover:border-slate-700'
              }`}>
                #{doc.numericOrder ?? 50}
              </span>

              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center justify-between text-slate-200 group-hover:text-white">
                  <span className="truncate">{doc.folderPath && doc.folderPath !== 'root' ? `${doc.folderPath}/${doc.name}` : doc.name}</span>
                  {doc.meta.status && (
                    <span className="ml-1 text-[9px] px-1.5 py-0.2 rounded font-mono uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shrink-0 font-bold">
                      {doc.meta.status}
                    </span>
                  )}
                </div>

                <p className="text-[11px] text-slate-400 truncate mt-0.5">{doc.meta.title}</p>

                <div className="flex items-center space-x-3 mt-1.5 text-[10px] text-slate-500 font-mono">
                  <span className="flex items-center space-x-1">
                    <Clock className="w-3 h-3 text-cyan-400" />
                    <span>{doc.meta.readingTimeMinutes}m</span>
                  </span>
                  {docTotal > 0 && (
                    <span className="flex items-center space-x-1">
                      <CheckSquare className="w-3 h-3 text-emerald-400" />
                      <span>{docCompleted}/{docTotal}</span>
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer Validation Status */}
      <div className="p-3 border-t border-white/10 text-[10px] font-mono text-slate-400 flex items-center justify-between bg-obsidian-950/60">
        <span className="flex items-center space-x-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span>Spec Package Validated</span>
        </span>
        <span className="text-slate-400">WCAG AA</span>
      </div>
    </aside>
  );
};
